import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { photoUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, MapPin, PawPrint, Eye } from "lucide-react";

export default function LostPets() {
  const [pets, setPets] = useState([]);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ message: "", contact: "", location: "" });
  const [sending, setSending] = useState(false);
  const [birdCode, setBirdCode] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/lost-pets").then((r) => setPets(r.data));
  }, []);

  const send = async (pet) => {
    if (!form.message) return toast.error("Descreva o avistamento");
    setSending(true);
    try {
      await api.post(`/pets/${pet.pet_id}/sighting`, form);
      toast.success("Tutor avisado! Obrigado por ajudar.");
      setOpen(null);
      setForm({ message: "", contact: "", location: "" });
    } catch {
      toast.error("Erro ao enviar");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="lost-pets-page">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="inline-flex items-center gap-2 bg-red-50 text-red-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
          <AlertTriangle className="w-3.5 h-3.5" /> Rede comunitária PetMachine
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold font-heading text-brand-graphite">Pets procurando o caminho de casa</h1>
        <p className="text-muted-foreground mt-3">Viu algum desses pets por Joinville? Avise o tutor — sem spam, só solidariedade.</p>
      </div>

      <div className="bg-white rounded-2xl border p-5 mb-8 flex flex-col sm:flex-row items-center gap-3" data-testid="bird-lookup-card">
        <div className="flex-1 text-center sm:text-left">
          <p className="font-heading font-semibold text-brand-graphite">Encontrou uma ave com anilha?</p>
          <p className="text-xs text-muted-foreground">Digite o código gravado na anilha (ex.: MY 026 ou PMH-000001) para ver o perfil e avisar o tutor — mesmo sem QR code.</p>
        </div>
        <Input value={birdCode} onChange={(e) => setBirdCode(e.target.value)} placeholder="Código da anilha" className="sm:w-52" data-testid="bird-code-input" />
        <Button onClick={() => { if (birdCode.trim()) navigate(`/anilha/${encodeURIComponent(birdCode.trim())}`); }} className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full shrink-0" data-testid="bird-code-search-btn">Buscar ave</Button>
      </div>

      {pets.length === 0 ? (
        <div className="text-center py-16 bg-brand-mintLight rounded-3xl" data-testid="no-lost-pets">
          <PawPrint className="w-10 h-10 text-brand-mint mx-auto mb-3" />
          <p className="font-heading font-semibold text-lg text-brand-graphite">Ótima notícia: nenhum pet desaparecido no momento!</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {pets.map((pet) => (
            <div key={pet.pet_id} className="bg-white rounded-2xl overflow-hidden border-2 border-red-200 card-hover" data-testid={`lost-pet-${pet.pet_id}`}>
              <div className="relative h-48 bg-brand-cream">
                {pet.photo ? <img src={photoUrl(pet.photo)} alt={pet.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><PawPrint className="w-10 h-10 text-brand-coral/40" /></div>}
                <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">DESAPARECIDO</span>
              </div>
              <div className="p-5">
                <h3 className="font-heading font-bold text-xl text-brand-graphite">{pet.name}</h3>
                <p className="text-sm text-muted-foreground">{pet.species}{pet.breed ? ` • ${pet.breed}` : ""}</p>
                {pet.lost?.location && <p className="text-xs text-red-600 mt-2 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Visto por último: {pet.lost.location}</p>}
                {pet.lost?.message && <p className="text-sm text-muted-foreground mt-2 italic">"{pet.lost.message}"</p>}
                <Dialog open={open === pet.pet_id} onOpenChange={(o) => setOpen(o ? pet.pet_id : null)}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full mt-4" data-testid={`sighting-btn-${pet.pet_id}`}>
                      <Eye className="w-4 h-4 mr-2" /> Eu vi este pet
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Avistar {pet.name}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <Textarea placeholder="Onde e quando você viu? Como ele parecia?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="sighting-message" />
                      <Input placeholder="Local do avistamento" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="sighting-location" />
                      <Input placeholder="Seu contato (opcional)" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="sighting-contact" />
                      <Button onClick={() => send(pet)} disabled={sending} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="sighting-submit">
                        {sending ? "Enviando..." : "Avisar o tutor"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
